import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/shared/utils/cn'

type ButtonVariant = 'solid' | 'soft' | 'outline' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg' | 'icon'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
}

const variantClass: Record<ButtonVariant, string> = {
  solid: 'bg-brand text-white hover:bg-brand-pressed active:bg-brand-pressed shadow-card',
  soft: 'bg-brand-soft text-brand-text hover:bg-blue-100 active:bg-blue-100',
  outline:
    'border border-border bg-surface text-text hover:bg-gray-50 active:bg-gray-100 shadow-card',
  ghost:
    'border-border-subtle bg-surface-subtle text-text shadow-card hover:bg-gray-100 active:bg-gray-200',
  danger: 'bg-danger text-white hover:brightness-95 active:brightness-90 shadow-card',
}

const sizeClass: Record<ButtonSize, string> = {
  sm: 'h-[34px] px-2.5 text-preset-label rounded-md sm:h-9 sm:px-3',
  md: 'h-10 px-3 text-preset-body-strong rounded-md sm:h-11 sm:px-4',
  lg: 'h-11 px-4 text-preset-body-strong rounded-lg sm:h-13 sm:px-5',
  icon: 'h-9 w-9 p-0 rounded-md sm:h-10 sm:w-10',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'solid',
      size = 'md',
      type = 'button',
      loading,
      disabled,
      leftIcon,
      rightIcon,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading
    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={cn(
          'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap border border-transparent font-semibold transition duration-base ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-45',
          variantClass[variant],
          sizeClass[size],
          className,
        )}
        {...props}
      >
        {loading ? (
          <span className="inline-flex size-4 shrink-0 items-center justify-center leading-none">
            <Loader2 aria-hidden className="size-4 animate-spin" />
          </span>
        ) : leftIcon ? (
          <span className="inline-flex size-4 shrink-0 items-center justify-center leading-none [&>svg]:size-4">
            {leftIcon}
          </span>
        ) : null}
        {children ? (
          <span className="inline-flex min-w-0 items-center leading-none">{children}</span>
        ) : null}
        {!loading && rightIcon ? (
          <span className="inline-flex size-4 shrink-0 items-center justify-center leading-none [&>svg]:size-4">
            {rightIcon}
          </span>
        ) : null}
      </button>
    )
  },
)

Button.displayName = 'Button'
